import { useMemo } from 'react';
import { useStore } from '@data/other/withStore';
import { useObserver } from 'mobx-react';
import NumberFormat from 'react-number-format';
import moment from 'moment';

const NumberPreset = {
  0: {
    thousandSeparator: ' ',
    decimalSeparator: ',',
    decimalScale: 3,
  },
  1: {
    thousandSeparator: '.',
    decimalSeparator: ',',
    decimalScale: 3,
  },
  2: {
    thousandSeparator: ',',
    decimalSeparator: '.',
    decimalScale: 2,
  },
  3: {
    thousandSeparator: '',
    decimalSeparator: '.',
    decimalScale: 2,
  },
  4: {
    thousandSeparator: ' ',
    decimalSeparator: '.',
    decimalScale: 2,
  },
  5: {
    thousandSeparator: '',
    decimalSeparator: ',',
    decimalScale: 2,
  },
  6: {
    thousandSeparator: '',
    decimalSeparator: `'`,
    decimalScale: 2,
  },
  7: {
    thousandSeparator: '.',
    decimalSeparator: `'`,
    decimalScale: 2,
  },
  8: {
    thousandSeparator: ',',
    decimalSeparator: `'`,
    decimalScale: 2,
  },
  9: {
    thousandSeparator: ' ',
    decimalSeparator: `'`,
    decimalScale: 2,
  },
  10: {
    thousandSeparator: '',
    decimalSeparator: ' ',
    decimalScale: 2,
  },
  11: {
    thousandSeparator: ',',
    decimalSeparator: ' ',
    decimalScale: 2,
  },
  12: {
    thousandSeparator: '.',
    decimalSeparator: ' ',
    decimalScale: 2,
  },
  13: {
    thousandSeparator: `'`,
    decimalSeparator: ' ',
    decimalScale: 2,
  },
} as const;

const NumberFormats = {
  PeriodDecimalNoThousand: NumberPreset[3],
  PeriodDecimalCommaThousand: NumberPreset[2],
  PeriodDecimalSpaceThousand: NumberPreset[4],
  PeriodDecimalApostropheThousand: NumberPreset[2],
  CommaDecimalNoThousand: NumberPreset[5],
  CommaDecimalSpaceThousand: NumberPreset[0],
  PeriodDecimalPeriodThousand: NumberPreset[4],
  ApostropheDecimalNoThousand: NumberPreset[6],
  ApostropheDecimalPeriodThousand: NumberPreset[7],
  ApostropheDecimalCommaThousand: NumberPreset[8],
  ApostropheDecimalSpaceThousand: NumberPreset[9],
  SpaceDecimalNoThousand: NumberPreset[10],
  SpaceDecimalCommaThousand: NumberPreset[11],
  SpaceDecimalPeriodThousand: NumberPreset[12],
  SpaceDecimalApostropheThousand: NumberPreset[13],
};

type NumberFormatType = {
  thousandSeparator: string;
  decimalSeparator: string;
  decimalScale: number;
};

type NumberFormatResult = NumberFormatType & {
  prefix?: string;
  suffix?: string;
};

const CurrencyPrefix = {
  usd: 'USD',
  brl: 'BRL',
  eur: 'EUR',
  uyu: 'UYU',
  uzs: 'UZS',
  vef: 'VEF',
  ars: 'ARS',
  aoa: 'AOA',
};

type CurrencyPrefixType = typeof CurrencyPrefix;
type CurrencyPrefixKeys = keyof CurrencyPrefixType;

const asPrefix = ['usd', 'brl'];

const DateFormats = {
  USEnglish: 'MM-DD-YY',
  USEnglish2: 'YYYY-MM-DD',
  Finnish: 'DD.MM.YYYY',
  French: 'DD/MM/YYYY',
  GBEnglish: 'DD/MM/YY',
  Italian: 'DD.MM.YY',
  Spanish: 'DD-MM-YY',
  USA: 'MM/DD/YYYY',
  HongKong: 'DD-MMM-YYYY',
};

const TimeFormats = {
  HHMM12hour: 'h:mm A',
  HHMM24hour: 'HH:mm',
};

export default function useFormat() {
  const { environment } = useStore();

  const currency = useMemo(
    () => environment.currency || 'usd',
    [environment.currency]
  );

  return useMemo(() => {
    const getters = {
      getDateFormat: () => DateFormats['USEnglish2'], // we will get date format from the server or settings in the future
      getDateTimeFormat: () => {
        return `${getters.getDateFormat()} ${TimeFormats['HHMM24hour']}`; // we will get the time format from the server or settings in the future
      },
      getNumberFormat: () => NumberFormats['PeriodDecimalCommaThousand'], // we will get this info from settings in the future.
      getCurrencyFormat: (): NumberFormatResult => ({
        ...getters.getNumberFormat(),
        suffix:
          asPrefix.indexOf(currency || '') === -1
            ? ' ' + CurrencyPrefix[currency as CurrencyPrefixKeys]
            : '',
        prefix:
          asPrefix.indexOf(currency || '') !== -1
            ? CurrencyPrefix[currency as CurrencyPrefixKeys] + ' '
            : '',
      }),
    };

    const format = {
      number: (value: string | number | undefined) =>
        new NumberFormat({
          value,
          fixedDecimalScale: true,
          ...getters.getNumberFormat(),
        }).state.value as string,
      currency: (value: string | number | undefined) =>
        new NumberFormat({
          value,
          fixedDecimalScale: true,
          ...getters.getCurrencyFormat(),
        }).state.value as string,
      date: (date: Date | undefined, formatString?: string | undefined) =>
        date
          ? moment(date).format(formatString || getters.getDateFormat())
          : '',
      dateTime: (date: Date | undefined) =>
        date ? moment(date).format(getters.getDateTimeFormat()) : '',
    };

    return {
      ...getters,
      ...format,
    };
  }, [currency]);
}
